import {
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { booleanCell, dateCell, baseCell, DataGridCell } from 'components/cells';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators,
  numberFilterOperators
} from 'components/enhanced';
import { booleanValueFormatter } from 'components/enhanced/data-grid/utils/helpers';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import ConfirmationUnlinkTableDialog from 'components/ui/feedback/ConfirmationGridUnlink';
import FileUploader from 'components/ui/file-uploader/FileUploader';
import { SectionTitle } from 'components/ui/typography';
import { IPurchaseOrder } from 'features/inventory/purchase-orders/types';
import { transportDocumentService } from 'features/inventory/transport-documents/TransportDocument/api/transport-document.service';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useIsTechnician, useIsTechnicianCoordinator } from 'shared/permissions';

export function TransportDocumentColumns(
  isLinkable: boolean,
  onUploadSuccess: any,
  unplugDDTfromInvoice?: (id) => void,
  showInvoiceColumns = true,
  showFileColumns = true
): GridColDef[] {
  const { t } = useTranslation();
  const isDisableEdit = useIsTechnicianCoordinator() || useIsTechnician();

  const columns: GridColDef[] = [
    {
      field: 'ref',
      headerName: t(`entities.ddt.ref`),
      width: 100,
      renderCell: (params: GridRenderCellParams<any>) =>
        isLinkable ? (
          <NavLink to={`./${params.row?.id}`}>
            <SectionTitle>{params.value}</SectionTitle>
          </NavLink>
        ) : (
          <SectionTitle>{params.value}</SectionTitle>
        )
    },
    {
      field: 'billable',
      headerName: t(`entities.ddt.billable`),
      width: 100,
      filterOperators: booleanFilterOperators,
      editable: true,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'closed',
      headerName: t(`entities.ddt.invoiced`),
      width: 100,
      filterOperators: booleanFilterOperators,
      renderCell: booleanCell(t),
      editable: true,
      type: 'boolean'
    },
    {
      field: 'date',
      headerName: t(`entities.ddt.date`),
      filterOperators: dateFilterOperators,
      width: 150,
      renderCell: dateCell
    },
    {
      field: 'date_arrival',
      headerName: t(`entities.ddt.date_arrival`),
      filterOperators: dateFilterOperators,
      width: 150,
      renderCell: dateCell
    },
    // {
    //   field: 'credit_memo_amount',
    //   headerName: t(`entities.ddt.credit_memo_amount`),
    //   filterOperators: numberFilterOperators,
    //   width: 100,
    //   valueFormatter: (params: GridValueFormatterParams) => {
    //     return currencyFormatter(params.value);
    //   },
    //   renderCell: currencyCell
    // },
    {
      field: 'order',
      headerName: t(`entities.ddt.order`),
      filterOperators,
      width: 150,
      valueFormatter: (params) => params.value?.ref ?? '',
      renderCell: (params: GridRenderCellParams<IPurchaseOrder>) => {
        const order = params.value;
        return (
          order &&
          (isLinkable ? (
            <NavLink to={`/tenant/orders/view/${order.id}`}>
              <SectionTitle>{order.ref}</SectionTitle>
            </NavLink>
          ) : (
            <SectionTitle>{order.ref}</SectionTitle>
          ))
        );
      }
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`entities.ddt.notes`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    ...(showInvoiceColumns
      ? [
          {
            field: 'invoice_ref',
            headerName: t(`entities.ddt.invoice_ref`),
            filterOperators,
            width: 150,
            renderCell: baseCell
          },
          {
            field: 'invoice_date',
            headerName: t(`entities.ddt.invoice_date`),
            filterOperators: dateFilterOperators,
            sortable: true,
            width: 150,
            renderCell: dateCell
          }
        ]
      : [])
  ];

  // Removed ', shipping_fare' 18/12/24 for ROMA 2 implementation (consegne splitted from fatturazione)
  ['amount', 'expected_amount'].forEach((colName) => {
    columns.push({
      field: colName,
      headerName: t(`entities.ddt.${colName}`),
      type: 'number',
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 150,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value);
      },
      renderCell: (params: GridRenderCellParams<any>) => (
        <DataGridCell content={currencyFormatter(params.value)} />
      )
    });
  });

  if (showFileColumns && onUploadSuccess) {
    ['invoice_file', 'ddt_file', 'invoice_revision_file'].forEach((file) => {
      columns.push({
        field: file,
        headerName: t(`entities.ddt.${file}`),
        filterOperators,
        filterable: false,
        sortable: false,
        width: 150,
        valueFormatter: booleanValueFormatter,
        renderCell: (params: GridRenderCellParams<any>) => (
          <FileUploader
            onUploadSuccess={onUploadSuccess}
            service={transportDocumentService}
            entityId={params.row?.id}
            attachmentName={file}
            filename={params.value}
            disableEdit={isDisableEdit}
          />
        )
      });
    });
  }

  if (unplugDDTfromInvoice) {
    columns.push({
      field: 'actions',
      type: 'actions',
      renderCell: (params: GridRenderCellParams<any>) => (
        <ConfirmationUnlinkTableDialog id={params.row?.id} handleUnlink={unplugDDTfromInvoice} />
      )
    });
  }

  return columns;
}
